import { piecesLineitem, CreatePiecesLineitemVM } from './interfaces'

const apiURL = process.env.REACT_APP_API_URL

// Get all pieces line items
export const getAllPiecesLineitems = async (): Promise<piecesLineitem[]> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch pieces line items')
  }
  
  return await response.json()
}

// Get pieces line item by ID
export const getPiecesLineitemById = async (id: number): Promise<piecesLineitem> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/${id}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch pieces line item')
  }
  
  return await response.json()
}

// Get pieces line items by pieces ID
export const getPiecesLineitemsByPiecesId = async (piecesId: number): Promise<piecesLineitem[]> => {
  const token = localStorage.getItem('authToken')
  
  console.log(`Fetching line items for pieces ID: ${piecesId}`)
  
  try {
    // Try the primary endpoint first
    const response = await fetch(`https://${apiURL}/PiecesLineitem/pieces/${piecesId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
    
    console.log(`API Response status: ${response.status}`)
    
    if (response.ok) {
      const data = await response.json()
      console.log('Received line items data:', data)
      
      // Ensure the data is an array and has the expected structure
      if (Array.isArray(data)) {
        return data
      } else {
        console.warn('API returned non-array data:', data)
        return []
      }
    } else {
      console.warn(`Pieces line items endpoint returned error (status: ${response.status})`)
      
      // Try alternative endpoint pattern
      const altResponse = await fetch(`https://${apiURL}/PiecesLineitem?piecesId=${piecesId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      
      if (altResponse.ok) {
        const altData = await altResponse.json()
        console.log('Received line items data from alternative endpoint:', altData)
        return Array.isArray(altData) ? altData : []
      }
    }
  } catch (error) {
    console.error('Error fetching line items:', error)
  }
  
  // If all endpoints fail, return empty array
  console.warn('All line item endpoints failed, returning empty array')
  return []
}

// Get pieces line items by project ID
export const getPiecesLineitemsByProjectId = async (projectId: number): Promise<piecesLineitem[]> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/project/${projectId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch pieces line items by project ID')
  }
  
  return await response.json()
}

// Get pieces line items by Xero Account ID
export const getPiecesLineitemsByXeroAccountId = async (xeroAccountId: number): Promise<piecesLineitem[]> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/xeroaccount/${xeroAccountId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch pieces line items by Xero account ID')
  }
  
  return await response.json()
}

// Get pieces line items by Xero Supplier ID
export const getPiecesLineitemsByXeroSupplierId = async (xeroSupplierId: number): Promise<piecesLineitem[]> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/xerosupplier/${xeroSupplierId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch pieces line items by Xero supplier ID')
  }
  
  return await response.json()
}

// Create pieces line item
export const createPiecesLineitem = async (lineitemData: CreatePiecesLineitemVM): Promise<piecesLineitem> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(lineitemData),
  })
  
  if (!response.ok) {
    throw new Error('Failed to create pieces line item')
  }
  
  return await response.json()
}

// Update pieces line item
export const updatePiecesLineitem = async (id: number, lineitemData: Partial<piecesLineitem>): Promise<piecesLineitem> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(lineitemData),
  })
  
  if (!response.ok) {
    throw new Error('Failed to update pieces line item')
  }
  
  return await response.json()
}

// Delete pieces line item
export const deletePiecesLineitem = async (id: number): Promise<void> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to delete pieces line item')
  }
}

// Financial calculation functions
export const getTotalNetByProjectId = async (projectId: number): Promise<number> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/project/${projectId}/total-net`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to get total net by project ID')
  }
  
  return await response.json()
}

export const getTotalTaxByProjectId = async (projectId: number): Promise<number> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/project/${projectId}/total-tax`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to get total tax by project ID')
  }
  
  return await response.json()
}

export const getTotalByProjectId = async (projectId: number): Promise<number> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/project/${projectId}/total`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to get total by project ID')
  }
  
  return await response.json()
}

export const getTotalQuantityByProjectId = async (projectId: number): Promise<number> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/PiecesLineitem/project/${projectId}/total-quantity`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to get total quantity by project ID')
  }
  
  return await response.json()
}